/**
 * Launch planning — "When can we launch?"
 *
 * The three additional steps beyond viability (Participation, Time-Phased
 * supply & profiles, Funding & Costs) as a guided rail with live readiness,
 * then the Launch Calendar as the answer surface. Same scenario, same
 * engine — this screen adds no machinery of its own.
 */

import { useNavigate } from 'react-router-dom';
import { CalendarClock } from 'lucide-react';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton } from '../components/primitives';
import { StepRail, type FlowStep } from '../components/flow';
import { useLaunchReadiness, useViabilityRun } from './useViabilityData';
import { ViabilityScenarioChoice } from './ViabilityScenarioChoice';

export default function LaunchPlanScreen() {
  const navigate = useNavigate();
  const app = getAppDefinition('viability')!;
  const { projectId, scenario, scenarios, scenariosLoaded, chooseScenario, chooseError } =
    useViabilityRun();
  const readiness = useLaunchReadiness(projectId, scenario?.id ?? null);

  if (scenariosLoaded && !scenario) {
    return (
      <AppShell app={app} backTo="/apps/viability">
        <ScreenHeading
          title="When can we launch?"
          subtitle="Launch planning needs a scenario to hold its assumptions."
        />
        <EmptyState
          title="Create a scenario first"
          hint="Run the viability check once from a scenario, then come back — the launch steps attach to it."
          action={
            <PrimaryButton onClick={() => navigate('/planning/engine/scenarios')}>
              Create a scenario
            </PrimaryButton>
          }
        />
      </AppShell>
    );
  }

  const profileCoverageShort =
    readiness.prioritiesTotal > 0 && readiness.prioritiesWithProfile < readiness.prioritiesTotal;

  const steps: FlowStep[] = [
    {
      key: 'participation',
      label: 'Participation',
      detail:
        readiness.participationCount > 0
          ? `${readiness.participationCount} team commitment${readiness.participationCount === 1 ? '' : 's'} recorded`
          : 'Who has actually committed to support these launches?',
      status: !readiness.loaded ? 'todo' : readiness.participationCount > 0 ? 'done' : 'warn',
    },
    {
      key: 'time-phased',
      label: 'Supply & workload templates',
      detail: !readiness.loaded
        ? 'Loading…'
        : readiness.supplyRows === 0
          ? 'No weekly supply calendar yet'
          : profileCoverageShort
            ? `${readiness.prioritiesWithProfile} of ${readiness.prioritiesTotal} launches have a profile`
            : 'Supply and profiles in place',
      status: !readiness.loaded
        ? 'todo'
        : readiness.supplyRows === 0 || profileCoverageShort
          ? 'warn'
          : 'done',
    },
    {
      key: 'funding',
      label: 'Funding & costs',
      detail:
        readiness.fundingEnvelopeCount > 0
          ? `${readiness.fundingEnvelopeCount} funding envelope${readiness.fundingEnvelopeCount === 1 ? '' : 's'} set`
          : 'Optional — add the money picture when it matters',
      status: !readiness.loaded ? 'todo' : readiness.fundingEnvelopeCount > 0 ? 'done' : 'warn',
    },
  ];

  return (
    <AppShell app={app} backTo="/apps/viability" backLabel="Back to Viability">
      <ScreenHeading
        title="When can we launch?"
        subtitle={
          scenario
            ? `Three more inputs on “${scenario.name}”, then the calendar answers.`
            : 'Loading your scenario…'
        }
      />
      <BallLine ballWith="you — work the three steps, then open the calendar" />

      <div className="space-y-4">
        <ViabilityScenarioChoice
          scenario={scenario}
          scenarios={scenarios}
          onChoose={(id) => void chooseScenario(id)}
          error={chooseError}
        />

        <div>
          <div className="mb-2">
            <Eyebrow>The three additional steps</Eyebrow>
          </div>
          <StepRail steps={steps} onSelect={(key) => navigate(`/planning/engine/${key}`)} />
          <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
            Each step opens the full editor for that input — come back when it's
            green enough.
          </p>
        </div>

        <AppCard>
          <Eyebrow>The answer</Eyebrow>
          <p className="mt-2 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
            The Launch Calendar places every launch week by week — what's
            scheduled, what slips, and how confident each date is.
          </p>
          <div className="mt-4">
            <PrimaryButton onClick={() => navigate('/planning/launch-calendar')}>
              <CalendarClock className="h-4 w-4" /> Open the Launch Calendar
            </PrimaryButton>
          </div>
        </AppCard>
      </div>
    </AppShell>
  );
}
